/**
 * Growth-curve helpers derived from the simulation constants. Pure functions, no state — the
 * server uses them to run the simulation and the client uses them to draw it, so both sides
 * agree on how big a player is without the size ever going over the wire.
 */

import {
  BASE_SPEED,
  DUEL_MIN_MASS,
  EAT_COVERAGE_RATIO,
  EAT_MASS_RATIO,
  MASS_DECAY_PER_SECOND,
  MASS_RADIUS_COEFFICIENT,
  MAX_MASS_DECAY_PER_SECOND,
  MAX_PLAYER_MASS,
  MIN_SPEED,
  PELLET_GROWTH_PER_TICK,
  PELLET_MASS_MAX,
  PELLET_MASS_MIN,
  PELLET_RADIUS_COEFFICIENT,
  PELLET_SIZE_STEPS,
  PELLET_WIRE_MASS_MAX,
  SPEED_MASS_EXPONENT,
  SPIKE_RADIUS,
  SPIKE_SHATTER_PENETRATION,
  STARTING_MASS,
} from './simulation.js';

/** Radius for a given mass. Area is proportional to mass, so radius goes as sqrt(mass). */
export function massToRadius(mass: number): number {
  return MASS_RADIUS_COEFFICIENT * Math.sqrt(Math.max(mass, 0));
}

/** Inverse of `massToRadius`, for turning a drawn or measured radius back into mass. */
export function radiusToMass(radius: number): number {
  const r = Math.max(radius, 0) / MASS_RADIUS_COEFFICIENT;
  return r * r;
}

/**
 * A pellet's draw and pickup radius. Area proportional to mass, exactly like a player's, but on
 * the pellets' own coefficient — see PELLET_RADIUS_COEFFICIENT for why they do not share one.
 */
export function pelletRadius(mass: number): number {
  return PELLET_RADIUS_COEFFICIENT * Math.sqrt(Math.max(mass, 0));
}

/**
 * What a pellet spawned at `spawnMass` on `spawnTick` is worth on `tick`.
 *
 * THE WHOLE OF PELLET GROWTH LIVES HERE, and it is a pure function of age on purpose: nothing
 * sweeps the pellet field. At PELLET_COUNT a per-tick drip would be a real cost every tick of
 * every room for a quantity almost nobody is looking at, whereas this costs exactly one multiply
 * at the two moments a pellet's current mass is ever needed — when something eats it, and when
 * it is serialised. It is also why the client can draw a growing pellet without being re-told:
 * it has the same curve and the same two numbers.
 *
 * Deterministic and replay-safe because it is stated in ticks. A negative age (a pellet from a
 * tick that has not happened) reads as zero rather than shrinking it below its spawn mass.
 *
 * The ceiling is the larger of PELLET_MASS_MAX and the pellet's own spawn mass, so this never
 * *shrinks* one. That is what an ejected pellet (EJECTED_PELLET_MASS, three times the ordinary
 * ceiling) rides: it is worth exactly what it was thrown at for its whole life, growing nowhere
 * because it is already past the top of the curve. Every ordinary pellet spawns inside the band
 * and is unaffected.
 */
export function pelletMassAt(spawnMass: number, spawnTick: number, tick: number): number {
  const age = Math.max(0, tick - spawnTick);
  const ceiling = Math.max(PELLET_MASS_MAX, spawnMass);
  return Math.min(ceiling, spawnMass + age * PELLET_GROWTH_PER_TICK);
}

/**
 * Mass -> the integer step that goes on the wire, and back.
 *
 * One byte across the band. The server keeps the exact mass — that is what a player is actually
 * paid when they eat one — and the quantised value exists only so the receiver can draw the dot
 * the right size for a third of the bytes a float would cost. See PELLET_SIZE_STEPS.
 *
 * The band runs to PELLET_WIRE_MASS_MAX, which is the *ejected* mass rather than the top of the
 * growth curve — so this one field is also what tells an oversized bait dot apart from an
 * ordinary one, and no pellet frame carries a flag saying so.
 */
export function pelletMassToSize(mass: number): number {
  const clamped = Math.min(Math.max(mass, PELLET_MASS_MIN), PELLET_WIRE_MASS_MAX);
  const fraction = (clamped - PELLET_MASS_MIN) / (PELLET_WIRE_MASS_MAX - PELLET_MASS_MIN);
  return Math.round(fraction * PELLET_SIZE_STEPS);
}

export function pelletSizeToMass(size: number): number {
  const step = Math.min(Math.max(size, 0), PELLET_SIZE_STEPS);
  return PELLET_MASS_MIN + (step / PELLET_SIZE_STEPS) * (PELLET_WIRE_MASS_MAX - PELLET_MASS_MIN);
}

/** Movement speed in world units per second: heavier is slower, with a floor at MIN_SPEED. */
export function massToSpeed(mass: number): number {
  const scaled = BASE_SPEED * (STARTING_MASS / Math.max(mass, 1)) ** SPEED_MASS_EXPONENT;
  return Math.max(scaled, MIN_SPEED);
}

/**
 * The share of its own mass a player sheds per second, as a *function of that mass*.
 *
 * THE CURVE IS A STRAIGHT LINE BETWEEN THE TWO ENDS OF THE MASS RANGE, and that is deliberately
 * the simplest shape that answers the report: MASS_DECAY_PER_SECOND at a starting dot,
 * MAX_MASS_DECAY_PER_SECOND at MAX_PLAYER_MASS, interpolated on mass over the ceiling. Because
 * the *rate* is what rises, the mass actually lost goes as very nearly the square of the mass —
 * which is what turns "shrinking by a fixed fraction of whatever you are" into "the top of the
 * range is expensive and the bottom is free":
 *
 *   mass 20 (a dot)   0.500 %/s     0.10 mass/s     (a flat 0.002 lost 0.04)
 *   mass 500          0.525 %/s     2.6  mass/s
 *   mass 2 000        0.600 %/s    12    mass/s
 *   mass 5 000        0.750 %/s    37    mass/s
 *   mass 10 000       1.000 %/s   100    mass/s
 *   mass 30 000 (MAX) 2.000 %/s   600    mass/s     (a flat 0.002 lost 60)
 *
 * HOW LONG A PARKED PLAYER TAKES TO COME BACK DOWN, simulated over the real 20 Hz tick with
 * nothing eaten at all — `room-sim.test.ts` pins the two ends of this table against the
 * simulation itself rather than against the arithmetic:
 *
 *   from MAX_PLAYER_MASS   to 20 000    24 s      to 10 000   1.4 min
 *                          to  3 000   3.9 min    to  1 000   7.0 min
 *                          to    100  14.4 min    to the floor (STARTING_MASS)  ~20 min
 *   from mass 500          to the floor           ~10.6 min
 *
 * So a parked leader is no longer a leader within a minute and is genuinely a dot inside a long
 * session, where the flat rate left them above 20 000 for three and a half minutes and needed an
 * hour to reach the floor. Nothing here can *kill*: the caller floors at STARTING_MASS, so being
 * eaten stays the only way a run ends.
 *
 * Pure arithmetic on the mass, no clock and no draw, so a replay of the same inputs decays
 * identically and `world-hash.ts` needs to know nothing about it.
 */
export function massDecayPerSecond(mass: number): number {
  const share = Math.min(Math.max(mass, 0) / MAX_PLAYER_MASS, 1);
  return MASS_DECAY_PER_SECOND + (MAX_MASS_DECAY_PER_SECOND - MASS_DECAY_PER_SECOND) * share;
}

/** True when the attacker outweighs the defender by at least EAT_MASS_RATIO. */
export function canEat(attackerMass: number, defenderMass: number): boolean {
  return attackerMass >= defenderMass * EAT_MASS_RATIO;
}

/**
 * True when neither player can eat the other and both are big enough for the collision to matter.
 *
 * This is the trigger for a mini-game duel, and the simulation asks it twice for the same
 * contact: once about the two *cells* that touched, which is what makes the collision
 * unresolvable, and once about the two *players*, which is what makes the duel fair — the stake
 * is a fraction of a whole cluster. A matched pair that fails the second test is simply bounced.
 */
export function isEvenlyMatched(massA: number, massB: number): boolean {
  if (massA < DUEL_MIN_MASS || massB < DUEL_MIN_MASS) return false;
  return !canEat(massA, massB) && !canEat(massB, massA);
}

/**
 * Area of the lens where two circles overlap: the standard circular-segment formula, and the
 * exact statement of what `coversMajority` means.
 *
 * `Math.acos` is the one transcendental in it. That is the same class of primitive the speed
 * curve already leans on (`massToSpeed` raises to a fractional power), so it adds no new kind of
 * cross-host risk — but it is why everything downstream of this is written to be a *pure*
 * function of the two radii and evaluated in a fixed order, so two hosts running the same engine
 * on the same seed get the same answer bit for bit.
 */
function lensArea(radiusA: number, radiusB: number, distance: number): number {
  if (distance >= radiusA + radiusB) return 0;
  const smaller = Math.min(radiusA, radiusB);
  // One circle entirely inside the other — including exactly coincident centres, where the
  // formula below would divide by zero.
  if (distance <= Math.abs(radiusA - radiusB)) return Math.PI * smaller * smaller;

  const a2 = radiusA * radiusA;
  const b2 = radiusB * radiusB;
  const d2 = distance * distance;
  const alpha = Math.acos(clampCosine((d2 + a2 - b2) / (2 * distance * radiusA)));
  const beta = Math.acos(clampCosine((d2 + b2 - a2) / (2 * distance * radiusB)));
  // Twice the area of the triangle on the two centres and one intersection point, by Heron's
  // formula. Clamped at zero: the product is non-negative for every distance that reaches here,
  // but rounding can take it a hair below at the very ends of the interval.
  const kite = (radiusA + radiusB - distance) * (distance + radiusA - radiusB);
  const wedge = kite * (distance - radiusA + radiusB) * (distance + radiusA + radiusB);
  return a2 * alpha + b2 * beta - 0.5 * Math.sqrt(Math.max(wedge, 0));
}

function clampCosine(value: number): number {
  if (value > 1) return 1;
  if (value < -1) return -1;
  return value;
}

/**
 * THE EAT RULE, geometrically: is at least EAT_COVERAGE_RATIO of the defender's circle inside
 * the attacker's?
 *
 * This is the definition; `eatContactDistance` is the same statement solved for distance and is
 * what the simulation actually runs. A degenerate defender (zero radius) is covered exactly when
 * its centre is inside the attacker — the limit of the rule as the defender shrinks to a point.
 */
export function coversMajority(
  attackerRadius: number,
  defenderRadius: number,
  distance: number,
): boolean {
  if (!(defenderRadius > 0)) return distance <= attackerRadius;
  const area = Math.PI * defenderRadius * defenderRadius;
  return lensArea(attackerRadius, defenderRadius, distance) >= area * EAT_COVERAGE_RATIO;
}

/**
 * How many halvings the reach solve does. 32 puts the answer within 2^-32 of the defender's
 * radius — around a nanometre at the sizes this game plays at, and far below anything a 20 Hz
 * tick can express, since a fresh player crosses 15 world units between two of them.
 */
const EAT_CONTACT_ITERATIONS = 32;

/**
 * The largest centre-to-centre distance at which `coversMajority` still holds — the reach of an
 * attacker of `attackerRadius` against a defender of `defenderRadius`.
 *
 * The coverage condition is monotone in distance and its closed form is not invertible in
 * elementary terms, so this is a fixed-count bisection rather than a formula: a deterministic,
 * allocation-free pure function of the two radii, with no early exit that could make the answer
 * depend on how the search happened to land. Callers compare *squared* distances against what it
 * returns, so the per-pair hot path stays free of a square root.
 *
 * The bracket is `[attackerRadius - defenderRadius, attackerRadius]`, and its top end is the
 * property the collision broadphase depends on. At a distance of exactly `attackerRadius` the
 * defender's centre sits on the attacker's rim, and the attacker's disc — being convex — lies
 * entirely on one side of the tangent line there, so it can cover at most half of the defender
 * and, for any finite attacker, strictly less. The reach is therefore always inside the
 * attacker's own radius, which is what the query in `stepPlayerCollisions` uses. That argument
 * is specific to EAT_COVERAGE_RATIO being at least a half; below a half this bracket and that
 * query both have to widen.
 *
 * Returns 0 when the attacker is too small to cover half of the defender at any distance (it is
 * smaller than the defender by more than a factor of sqrt(2)), which the eat path never reaches
 * because it asks EAT_MASS_RATIO first. Callers must read a non-positive reach as "never".
 */
export function eatContactDistance(attackerRadius: number, defenderRadius: number): number {
  const attacker = Math.max(attackerRadius, 0);
  const defender = Math.max(defenderRadius, 0);
  if (defender === 0) return attacker;

  let low = Math.max(attacker - defender, 0);
  if (!coversMajority(attacker, defender, low)) return 0;
  let high = attacker;
  for (let i = 0; i < EAT_CONTACT_ITERATIONS; i += 1) {
    const mid = (low + high) / 2;
    if (coversMajority(attacker, defender, mid)) low = mid;
    else high = mid;
  }
  return low;
}

/**
 * Centre-to-centre distance at which a spike is SPIKE_SHATTER_PENETRATION of the way into a cell
 * of `cellRadius` — which is to say, the distance at or inside which that cell shatters.
 *
 * The one place the threshold lives, because two very different pieces of code have to agree on
 * it: the simulation, which decides when the cell comes apart, and the renderer, which indents
 * the cell's outline around the spike on the way in. If they disagreed the blob would visibly
 * swallow the hazard before breaking, or break before touching it.
 *
 * `spikeOverlapDepth` is the same geometry read the other way, and is what the renderer bites the
 * outline with: how far the spike has pushed past the cell's edge, in world units, 0 when they
 * are merely touching and twice the spike's radius when the spike is entirely inside.
 *
 * **THE SPIKE'S OWN RADIUS IS AN ARGUMENT NOW, BECAUSE A FED SPIKE SWELLS** (`spikeRadiusFor`).
 * It defaults to SPIKE_RADIUS, so every caller that has nothing to feed it reads exactly as it
 * did. What is worth noticing here is that **this threshold does not actually depend on it**: at
 * SPIKE_SHATTER_PENETRATION of 0.5 the radius term is zero, so a shatter is measured to the
 * spike's *centre* — which is precisely what makes swelling a spike safe. A fed spike is bigger to
 * look at, bigger to dent an outline against and bigger to feed, and it breaks a cell at the same
 * moment a fresh one would. The argument is threaded through anyway rather than dropped, so that
 * moving the penetration constant later cannot silently make a swollen spike lie.
 */
export function spikeShatterDistance(cellRadius: number, spikeRadius = SPIKE_RADIUS): number {
  return cellRadius + spikeRadius * (1 - 2 * SPIKE_SHATTER_PENETRATION);
}

export function spikeOverlapDepth(
  cellRadius: number,
  distance: number,
  spikeRadius = SPIKE_RADIUS,
): number {
  return cellRadius + spikeRadius - distance;
}

/**
 * Centre-to-centre distance at which a spike stops touching a cell of `cellRadius` at all — the
 * outer edge of contact, where `spikeShatterDistance` is the inner one.
 *
 * The same geometry as `spikeOverlapDepth` read as a threshold, and it is shared for the reason
 * the shatter distance is: two pieces of code have to agree about it. The renderer indents the
 * outline for exactly as long as a spike is inside this distance, and the simulation holds a
 * cell's "already resolved against this spike" mark for exactly as long as the same is true —
 * so the tick a player stops seeing the dent is the tick that spike may break them again.
 *
 * A fed spike's own radius is the second argument — see `spikeShatterDistance` above.
 */
export function spikeContactDistance(cellRadius: number, spikeRadius = SPIKE_RADIUS): number {
  return cellRadius + spikeRadius;
}

/**
 * The shape every cluster helper needs. Both `CellSnapshot` (the wire) and the simulation's own
 * `CellState` satisfy it, so one implementation serves the server and the browser.
 */
export interface MassPoint {
  x: number;
  y: number;
  mass: number;
}

/**
 * Total mass of a cluster. A player's mass *is* the sum of its cells, which is why neither the
 * wire nor the renderer carries a separate player-level mass — see `PlayerSnapshot`.
 */
export function clusterMass(cells: readonly MassPoint[]): number {
  if (cells.length === 1) return cells[0].mass;
  let total = 0;
  for (const cell of cells) total += cell.mass;
  return total;
}

/**
 * Mass-weighted centre of a cluster — what the camera follows and what "where is that player"
 * means once a player is more than one circle. Weighted rather than plain-averaged so a large
 * cell trailing a few crumbs does not drag the view off the body that matters.
 *
 * X and Y are separate functions so a caller pays no allocation for a point object it is about
 * to destructure anyway; an empty cluster (which the simulation never produces) reads as 0.
 */
export function clusterCentroidX(cells: readonly MassPoint[]): number {
  if (cells.length === 1) return cells[0].x;
  let mass = 0;
  let weighted = 0;
  for (const cell of cells) {
    mass += cell.mass;
    weighted += cell.x * cell.mass;
  }
  return mass > 0 ? weighted / mass : 0;
}

export function clusterCentroidY(cells: readonly MassPoint[]): number {
  if (cells.length === 1) return cells[0].y;
  let mass = 0;
  let weighted = 0;
  for (const cell of cells) {
    mass += cell.mass;
    weighted += cell.y * cell.mass;
  }
  return mass > 0 ? weighted / mass : 0;
}

/**
 * How fast a cluster's *centroid* travels along its heading, in world units per second.
 *
 * Every cell steers on the shared heading but at `massToSpeed(its own mass)`, so the point the
 * camera follows moves at the mass-weighted mean of those speeds — differentiate the centroid
 * and the cells' masses fall out as the weights. That is the whole of the derivation, and it is
 * why the browser's prediction has to call this rather than `massToSpeed(clusterMass(cells))`:
 * the cluster does not have one speed, and predicting as though it did makes the prediction
 * fight the server for as long as a player is more than one circle.
 *
 * A single-cell cluster is `massToSpeed(mass)` exactly, so the overwhelmingly common case is
 * bit-identical to what it was before clusters existed.
 */
export function clusterSpeed(cells: readonly MassPoint[]): number {
  if (cells.length === 1) return massToSpeed(cells[0].mass);
  let mass = 0;
  let weighted = 0;
  for (const cell of cells) {
    mass += cell.mass;
    weighted += massToSpeed(cell.mass) * cell.mass;
  }
  return mass > 0 ? weighted / mass : 0;
}

/**
 * The radius of the smallest circle about a cluster's centroid that contains every one of its
 * cells, **edges included** — what a camera has to frame to show the whole player.
 *
 * THIS IS THE NUMBER THE VIEW IS SIZED FROM, and it is not `massToRadius(clusterMass(cells))`.
 * That is the radius the cluster would have if it were whole, and it says nothing at all about
 * how far apart the pieces of a split one are: two halves thrown SPLIT_BURST_DISTANCE apart have
 * exactly the mass they had a tick earlier and are spread over several times the width. Sizing a
 * view from mass is what left a player steering half a cluster they could not see — the second
 * half of the report `view.ts` was rewritten for.
 *
 * Measured about the centroid because that is what the camera is centred on (`clusterCentroidX`
 * / `Y`, and the client's prediction is an offset of the same point), so this radius and the
 * frame drawn around it share an origin. For a single cell the centroid IS the cell and this is
 * `massToRadius(mass)` exactly — which is what keeps a whole player framed to the float exactly
 * as they were before clusters were framed on their extent.
 */
export function clusterViewRadius(cells: readonly MassPoint[]): number {
  if (cells.length === 1) return massToRadius(cells[0].mass);
  const centreX = clusterCentroidX(cells);
  const centreY = clusterCentroidY(cells);
  let radius = 0;
  for (const cell of cells) {
    const reach = Math.hypot(cell.x - centreX, cell.y - centreY) + massToRadius(cell.mass);
    if (reach > radius) radius = reach;
  }
  return radius;
}
